import * as z from 'zod';
import { BillingTerm } from '../../constants/billing-term.enum';
import { centsToEuroInput, parseEuroInputToCents } from '../../lib/money';
import { CreateProductRequestDto } from '../../types/request';
import { BillingTermMetaResponseDto, ProductDetailsResponseDto } from '../../types/response';

const priceRowSchema = z.object({
  term: z.nativeEnum(BillingTerm),
  isEnabled: z.boolean(),
  monthlyPrice: z.string(),
  additionalConditions: z.string().max(300, 'Maximal 300 Zeichen')
});

/** One row per billing term (from the meta endpoint), so every term can be offered at most once. */
export const productFormSchema = z.object({
  title: z.string().trim().min(1, 'Titel ist erforderlich').max(120, 'Maximal 120 Zeichen'),
  description: z.string().max(2000, 'Maximal 2000 Zeichen'),
  capacity: z
    .string()
    .trim()
    .regex(/^\d+$/, 'Bitte eine ganze Zahl eingeben')
    .refine((value) => Number(value) >= 1, 'Mindestens 1 Slot'),
  prices: z.array(priceRowSchema).superRefine((rows, ctx) => {
    if (!rows.some((row) => row.isEnabled)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Mindestens eine Laufzeit aktivieren' });
    }
    rows.forEach((row, index) => {
      if (row.isEnabled && parseEuroInputToCents(row.monthlyPrice) === null) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [index, 'monthlyPrice'], message: 'Bitte einen gültigen Betrag eingeben, z. B. 499,00' });
      }
    });
  })
});

export type ProductFormValues = z.infer<typeof productFormSchema>;

export const buildProductFormDefaults = (
  billingTerms: BillingTermMetaResponseDto[],
  product?: ProductDetailsResponseDto | null
): ProductFormValues => ({
  title: product?.title ?? '',
  description: product?.description ?? '',
  capacity: product ? String(product.capacity) : '1',
  prices: billingTerms.map(({ term }) => {
    const price = product?.prices.find((p) => p.term === term);
    return {
      term,
      isEnabled: !!price,
      monthlyPrice: price ? centsToEuroInput(price.monthlyPriceCents) : '',
      additionalConditions: price?.additionalConditions ?? ''
    };
  })
});

export const toProductRequest = (values: ProductFormValues): CreateProductRequestDto => ({
  title: values.title.trim(),
  description: values.description.trim(),
  capacity: Number(values.capacity),
  prices: values.prices
    .filter((row) => row.isEnabled)
    .map((row) => ({
      term: row.term,
      monthlyPriceCents: parseEuroInputToCents(row.monthlyPrice) ?? 0,
      additionalConditions: row.additionalConditions.trim() || null
    }))
});
